import { __spreadArray } from 'tslib';
import '@eg-maps/google-maps';
import '@eg-maps/mapbox';
import { EGMap } from '@eg-maps/core';
import { mergeAndConcat } from 'merge-anything';
import * as React from 'react';
import { classNames } from 'uitk-react-utils';
import { useBrand } from '@shared-ui/brand-context';
import { EGDistances } from 'eg-distances';
import { defaultEGMapsConfig, sortDeeplyObjectAndArray, stringify } from '../common/uitk-eg-map-config.esm.js';
import 'react-dom';
import 'uitk-react-icons';
import 'uitk-react-loader';
import 'uitk-react-layout-position';
import '../types/uitk-eg-map-marker-type.esm.js';
import { UitkEGMapsType, UitkEGMapsContext } from '../types/uitk-eg-map-props.esm.js';
var defaultMapboxStyle = 'mapbox://styles/mapbox/streets-v11';
var gmapCfg = function () {
  var _a, _b, _c, _d;
  return {
    mapProvider: 'google-maps',
    googleMaps: {
      gmapsAddConfig: {
        streetViewControl: true,
        streetViewControlOptions: {
          position: (_b = (_a = window === null || window === void 0 ? void 0 : window.google) === null || _a === void 0 ? void 0 : _a.maps) === null || _b === void 0 ? void 0 : _b.ControlPosition.RIGHT_TOP
        },
        zoomControlOptions: {
          position: (_d = (_c = window === null || window === void 0 ? void 0 : window.google) === null || _c === void 0 ? void 0 : _c.maps) === null || _d === void 0 ? void 0 : _d.ControlPosition.RIGHT_TOP
        },
        scaleControl: true
      }
    }
  };
};
var mapboxCfg = function (accessToken) {
  return {
    mapProvider: 'mapbox',
    mapbox: {
      accessToken: accessToken,
      mapboxAddConf: {
        style: defaultMapboxStyle
      }
    }
  };
};
var egDistancesConfig = function (provider, accessToken) {
  var _a;
  return {
    routes: {
      egDistances: provider === 'mapbox' ? new EGDistances({
        provider: EGDistances.providers.mapbox,
        providers: (_a = {}, _a[EGDistances.providers.mapbox] = {
          key: accessToken
        }, _a)
      }) : new EGDistances({
        provider: EGDistances.providers.google
      })
    }
  };
};
var UitkEGMapsContainer = function (_a) {
  var _b = _a.height,
    height = _b === void 0 ? '100%' : _b,
    _c = _a.width,
    width = _c === void 0 ? '100%' : _c,
    _d = _a.egDistancesEnabled,
    egDistancesEnabled = _d === void 0 ? false : _d,
    initialViewport = _a.initialViewport,
    accessToken = _a.accessToken,
    children = _a.children,
    mapType = _a.mapType,
    features = _a.features,
    onFeaturesPushed = _a.onFeaturesPushed,
    _e = _a.mapConfig,
    mapConfig = _e === void 0 ? {} : _e,
    className = _a.className,
    innerRef = _a.innerRef,
    beforeInit = _a.beforeInit,
    onInit = _a.onInit,
    clientPadding = _a.clientPadding,
    onError = _a.onError;
  var mapProvider = mapType === UitkEGMapsType.GOOGLE ? 'google-maps' : 'mapbox';
  var _f = React.useState(),
    mapContext = _f[0],
    setMapContext = _f[1];
  var _g = React.useState(),
    initialConfig = _g[0],
    setInitialConfig = _g[1];
  var _h = React.useState(),
    providedConfig = _h[0],
    setProvidedConfig = _h[1];
  var mapMemoValue = React.useMemo(function () {
    return {
      mapContext: mapContext,
      setMapContext: setMapContext
    };
  }, [mapContext, setMapContext]);
  var mapRef = React.useRef();
  var brand = useBrand();
  var idSuffix = React.useMemo(function () {
    return Math.floor(Math.random() * 90);
  }, []);
  React.useImperativeHandle(innerRef, function () {
    return {
      map: function () {
        return (mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) || null;
      }
    };
  });
  var baseConfig = React.useMemo(function () {
    var ret;
    if (mapRef.current) {
      var egDistancesCfg = egDistancesEnabled ? egDistancesConfig(mapProvider, accessToken) : {};
      var mapProviderCfg = mapProvider === 'mapbox' ? mapboxCfg(accessToken) : gmapCfg();
      var defaultCfg = defaultEGMapsConfig(brand, function (cssPropertyName) {
        var _a;
        return (_a = getComputedStyle(mapRef.current)) === null || _a === void 0 ? void 0 : _a.getPropertyValue(cssPropertyName);
      });
      ret = mergeAndConcat(defaultCfg, mapProviderCfg, egDistancesCfg);
    }
    return ret;
  }, [mapRef.current, brand, mapProvider, accessToken, egDistancesEnabled]);
  React.useEffect(function () {
    if (mapConfig) {
      var cfg = mapConfig ? sortDeeplyObjectAndArray(mapConfig) : {};
      if (providedConfig === undefined || stringify(cfg) !== stringify(providedConfig)) {
        setProvidedConfig(cfg);
      }
    }
  }, [mapConfig, providedConfig]);
  var config = React.useMemo(function () {
    var _a, _b, _c, _d;
    var ret;
    if (baseConfig) {
      var configs = [];
      if (providedConfig) {
        configs.push(providedConfig);
        if (((_b = (_a = providedConfig.googleMaps) === null || _a === void 0 ? void 0 : _a.gmapsAddConfig) === null || _b === void 0 ? void 0 : _b.mapId) === null) configs.push({
          googleMaps: {
            gmapsAddConfig: {
              mapId: undefined
            }
          }
        });
        if (((_d = (_c = providedConfig.mapbox) === null || _c === void 0 ? void 0 : _c.mapboxAddConf) === null || _d === void 0 ? void 0 : _d.style) === null) configs.push({
          mapbox: {
            mapboxAddConf: {
              style: undefined
            }
          }
        });
      }
      ret = mergeAndConcat.apply(void 0, __spreadArray([baseConfig], configs, false));
    }
    return ret;
  }, [baseConfig, providedConfig]);
  React.useEffect(function () {
    var mapObj = (mapContext || {}).mapObj;
    if (config && config !== initialConfig) {
      if (mapObj) {
        mapObj.updateConfiguration(config);
      } else if (!initialConfig) {
        setInitialConfig(config);
      }
    }
  }, [initialConfig, config, mapContext]);
  React.useEffect(function () {
    if (initialConfig) {
      if (mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) {
        mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj.unbindAll();
      }
      try {
        var mapObj_1 = new EGMap(initialConfig);
        if (beforeInit) beforeInit(mapObj_1);
        mapObj_1.initMap(mapRef.current, true, initialViewport, clientPadding).then(function () {
          setMapContext({
            mapObj: mapObj_1,
            mapConfig: initialConfig
          });
          if (onInit) onInit(mapObj_1);
        }).catch(function (err) {
          if (onError) onError(err);
        });
      } catch (err) {
        if (onError) onError(err);
      }
      return function () {
        var _a;
        (_a = mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) === null || _a === void 0 ? void 0 : _a.unbindAll();
      };
    }
  }, [initialConfig]);
  React.useEffect(function () {
    if ((mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) && features) {
      var bounds = mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj.pushFeatureData(features, 'replace');
      if (bounds) onFeaturesPushed === null || onFeaturesPushed === void 0 ? void 0 : onFeaturesPushed(mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj, bounds);
    }
  }, [mapContext, features, onFeaturesPushed]);
  return React.createElement("div", {
    className: classNames('uitk-eg-maps-container', 'uitk-eg-maps', className),
    style: {
      width: width,
      height: height
    },
    id: "uitk-eg-maps-container-".concat(idSuffix),
    ref: mapRef
  }, (mapContext === null || mapContext === void 0 ? void 0 : mapContext.mapObj) && React.createElement(UitkEGMapsContext.Provider, {
    value: mapMemoValue
  }, children));
};
export { UitkEGMapsContainer, defaultMapboxStyle };